/* eslint-disable no-useless-escape */
import React from "react";
import AppReducer from "./reducer";
import Axios from "axios";

// High-order functions in context
import DateFormatter from "../functions/NewDateFormatter";
import translate from "../lang/translator";
import CalculateLicense from "../functions/CalculateLicense";

// Multilangual Files
import tr from "../lang/locales/tr";
import en from "../lang/locales/en";
import ru from "../lang/locales/ru";
import ar from "../lang/locales/ar";

const AppContext = React.createContext();
const CryptoJS = require("crypto-js");

export class AppProvider extends React.Component {
  constructor(props) {
    super();
    this.state = localStorage.getItem("state2")
      ? {
        ...initialState,
        ...JSON.parse(
          CryptoJS.AES.decrypt(
            localStorage.getItem("state2"),
            "S@LON"
          ).toString(CryptoJS.enc.Utf8)
        ),

        // New Keys Persist
        locales: { tr, en, ru, ar },
      }
      : { ...initialState };

    // Ensure we set Authorization header only when a token exists
    if (this.state.token) {
      Axios.defaults.headers.common = Axios.defaults.headers.common || {};
      Axios.defaults.headers.common.Authorization = `Bearer ${this.state.token}`;
    }

    // Global flag for logout state - prevents multiple logout attempts
    this.isLoggingOut = false;
    this.logoutTimestamp = null;

    // Interceptor ID'leri sakla (eject için)
    this.requestInterceptorId = null;
    this.responseInterceptorId = null;

    this.setupAxiosInterceptor();
    this.setupAxiosRequestInterceptor();
  }

  componentWillUnmount() {
    // Component unmount olurken interceptor'ları temizle
    if (this.requestInterceptorId !== null) {
      Axios.interceptors.request.eject(this.requestInterceptorId);
    }
    if (this.responseInterceptorId !== null) {
      Axios.interceptors.response.eject(this.responseInterceptorId);
    }
  }

  // JWT token'ın expire olup olmadığını kontrol et
  isTokenExpired = () => {
    if (!this.state.token || !this.state.is_authenticated) {
      return false;
    }

    try {
      // JWT'nin payload kısmını decode et (base64url)
      const tokenParts = this.state.token.split('.');
      if (tokenParts.length !== 3) {
        console.warn("Geçersiz JWT formatı - 3 parça olmalı");
        return false; // Format hatası varsa logout yapma, devam et
      }

      // Base64url'yi Base64'e çevir (JWT standart formatı)
      // - ve _ karakterlerini + ve / ile değiştir, padding ekle
      let base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');

      // Padding ekle (base64 4'ün katı olmalı)
      while (base64.length % 4) {
        base64 += '=';
      }

      const payload = JSON.parse(atob(base64));

      // JWT'de exp timestamp saniye cinsinden, JavaScript millisaniye kullanır
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // millisaniye'ye çevir
        const currentTime = Date.now();
        const isExpired = currentTime >= expirationTime;

        // Sadece expired olduğunda log'la
        if (isExpired) {
          console.log("⚠️ JWT Token Expired:", {
            currentTime: new Date(currentTime).toISOString(),
            expirationTime: new Date(expirationTime).toISOString(),
            expiredSince: Math.floor((currentTime - expirationTime) / 1000) + " saniye önce"
          });
        }

        return isExpired;
      }

      return false;
    } catch (e) {
      console.error("JWT decode hatası:", e, "Token:", this.state.token?.substring(0, 50) + "...");
      // Decode edilemiyorsa logout YAPMA, token geçerli olabilir
      // Sadece backend'den explicit hata gelirse logout yap
      return false;
    }
  };

  // Request öncesi JWT kontrolü yap
  setupAxiosRequestInterceptor = () => {
    // Eski interceptor varsa temizle
    if (this.requestInterceptorId !== null) {
      Axios.interceptors.request.eject(this.requestInterceptorId);
    }

    this.requestInterceptorId = Axios.interceptors.request.use(
      (config) => {
        // Her request öncesinde JWT'yi kontrol et (sadece authenticated ise)
        if (this.state.is_authenticated) {
          const tokenExpired = this.isTokenExpired();

          if (tokenExpired) {
            console.log("🚫 Request atılamaz: JWT token expired", config.url);

            // Eğer logout işlemi başlatılmamışsa, başlat
            if (!this.isLoggingOut) {
              this.handleTokenExpiration();
            }

            // Request'i iptal et
            return Promise.reject({
              message: "JWT token expired",
              config: config,
              isTokenExpired: true
            });
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  };

  // Token expiration durumunu handle et
  handleTokenExpiration = async () => {
    if (this.isLoggingOut) {
      return;
    }

    const now = Date.now();
    if (this.logoutTimestamp && (now - this.logoutTimestamp) < 5000) {
      console.log("Logout zaten yakın zamanda yapıldı.");
      return;
    }

    console.log("🔴 Token expired - otomatik yeniden giriş deneniyor...");
    this.isLoggingOut = true;
    this.logoutTimestamp = Date.now();

    // Önce axios header'ını temizle
    try {
      if (Axios.defaults && Axios.defaults.headers && Axios.defaults.headers.common) {
        delete Axios.defaults.headers.common.Authorization;
      }
      if (Axios.defaults && Axios.defaults.headers) {
        delete Axios.defaults.headers.Authorization;
      }
    } catch (e) {
      // ignore
    }

    // Kayıtlı credentials'ı kontrol et ve otomatik giriş dene
    try {
      const CryptoJS = require("crypto-js");
      const CREDENTIALS_KEY = "SR_AUTO_LOGIN";
      const CRYPTO_SECRET = "S@LON_AUTO";

      const encrypted = localStorage.getItem(CREDENTIALS_KEY);
      if (encrypted) {
        const decrypted = CryptoJS.AES.decrypt(encrypted, CRYPTO_SECRET).toString(CryptoJS.enc.Utf8);
        const credentials = JSON.parse(decrypted);

        if (credentials && credentials.mail && credentials.password) {
          console.log("Kayıtlı credentials bulundu, otomatik giriş deneniyor...");

          // Header olmadan yeni istek yap
          const response = await Axios.post(
            `${this.state.api_endpoint}/auth/staff`,
            credentials
          );

          if (response.status === 201) {
            const { data } = response;
            const AuthWithToken = require("../functions/AuthWithToken").default;
            await AuthWithToken(data?.data?.token, this);
            console.log("✅ Otomatik yeniden giriş başarılı!");
            this.isLoggingOut = false;
            return; // Başarılı, logout yapma
          }
        }
      }
    } catch (e) {
      console.error("Otomatik yeniden giriş başarısız:", e);
      // Sadece 401 veya authentication hatası ise credentials'ı temizle
      // Diğer hatalar (network vb.) için temizleme
      if (e.response && (e.response.status === 401 || e.response.status === 403)) {
        console.log("Credentials geçersiz, temizleniyor...");
        localStorage.removeItem("SR_AUTO_LOGIN");
      }
    }

    // Otomatik giriş başarısız, normal logout işlemine devam et
    console.log("Otomatik giriş başarısız, logout yapılıyor...");

    // Global flag'i temizle - login sayfasında tekrar otomatik giriş yapılabilsin
    delete window.__AUTO_LOGIN_ATTEMPTED__;

    // Check current page
    const currentPath = window.location.pathname;
    const isOnAuthPage = currentPath === "/login" ||
      currentPath === "/wallet" ||
      currentPath === "/" ||
      currentPath.startsWith("/forgot-pass") ||
      currentPath.startsWith("/register");

    // Dispatch logout
    this.dispatch({ type: "LOGOUT_USER" }).then(() => {
      console.log("Logout dispatch tamamlandı.");

      if (!isOnAuthPage) {
        setTimeout(() => {
          console.log("Token expired, login sayfasına yönlendiriliyor...");
          window.location.href = "/login";
          this.isLoggingOut = false;
        }, 150);
      } else {
        console.log("Zaten login/wallet sayfasındayız, redirect yapılmayacak.");
        setTimeout(() => {
          this.isLoggingOut = false;
        }, 500);
      }
    }).catch((err) => {
      console.error("Logout dispatch hatası:", err);
      this.isLoggingOut = false;
    });
  };

  setupAxiosInterceptor = () => {
    // Eski interceptor varsa temizle
    if (this.responseInterceptorId !== null) {
      Axios.interceptors.response.eject(this.responseInterceptorId);
    }

    this.responseInterceptorId = Axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        try {
          // Sadece JWT'nin expire olup olmadığını kontrol et (401 status'e bakmıyoruz)
          const jwtExpired = this.isTokenExpired();

          const resp = error.response;

          // Normalize message from backend if present
          const rawMsg = (resp && resp.data && (resp.data.message || resp.data.error)) || "";
          const msg = (rawMsg || "").toString().toLowerCase();

          // SADECE JWT expired ise veya backend message'da explicit JWT hatası varsa
          // "invalid token" kontrolü kaldırıldı - e-imza token hatalarını da yakalıyordu
          const isAuthError =
            jwtExpired ||
            msg.includes("jwt") ||
            msg.includes("token expired") ||
            msg.includes("token has expired");

          // Prevent logout if already in progress or recently done (within 5 seconds)
          const now = Date.now();
          if (this.logoutTimestamp && (now - this.logoutTimestamp) < 5000) {
            console.log("Logout zaten yakın zamanda yapıldı, tekrar yapılmayacak.");
            return Promise.reject(error);
          }

          if (isAuthError && this.state.is_authenticated && !this.isLoggingOut) {
            console.log("Response interceptor - Token expiration detected:", {
              url: error.config?.url,
              method: error.config?.method,
              status: resp && resp.status,
              message: rawMsg,
              jwtExpired: jwtExpired,
              token: this.state.token ? "Var" : "Yok",
              currentPath: window.location.pathname,
              reason: jwtExpired ? "JWT decoded ve expired" : "Backend token error message"
            });

            // Lisans kontrolü yap (wallet'a yönlendirmek için)
            let shouldGoToWallet = false;

            try {
              if (msg.includes("lisans") || msg.includes("license") || msg.includes("lisan")) {
                shouldGoToWallet = true;
              }
            } catch (e) {
              // ignore
            }

            if (!shouldGoToWallet && this.state.company_license && this.state.company_license.end_date) {
              const licenseCalc = CalculateLicense(this.state.company_license);
              const remainingDays = licenseCalc.getRemainingDaysFromNow();
              if (remainingDays <= 0) {
                shouldGoToWallet = true;
              }
            }

            // Eğer lisans problemi varsa wallet'a yönlendir
            if (shouldGoToWallet) {
              console.log("Lisans problemi tespit edildi, wallet'a yönlendiriliyor...");
              this.isLoggingOut = true;
              this.logoutTimestamp = Date.now();

              // Clear axios authorization
              try {
                if (Axios.defaults && Axios.defaults.headers && Axios.defaults.headers.common) {
                  delete Axios.defaults.headers.common.Authorization;
                }
              } catch (e) {
                // ignore
              }

              this.dispatch({ type: "LOGOUT_USER" }).then(() => {
                setTimeout(() => {
                  window.location.href = "/wallet?license_expired=true";
                  this.isLoggingOut = false;
                }, 150);
              });
            } else {
              // Normal token expiration - handleTokenExpiration kullan
              this.handleTokenExpiration();
            }
          }
        } catch (e) {
          // if any error happens during handling, just ignore and forward original error
          console.error("Error handling auth interceptor:", e);
          // Reset flags on error after delay
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 1000);
        }

        return Promise.reject(error);
      }
    );
  };

  dispatch = async (action) => {
    localStorage.removeItem("state2");

    return new Promise((resolve) => {
      this.setState((state) => AppReducer(state, action), () => {
        localStorage.setItem(
          "state2",
          CryptoJS.AES.encrypt(JSON.stringify(this.state), "S@LON").toString()
        );
        resolve();
      });
    });
  };

  render() {
    const context = {
      // eslint-disable-next-line
      api_endpoint:
        process.env.APP_NAME === "salon"
          ? this.state.APPLICATION.SALON_RANDEVU.API
          : process.env.APP_NAME === "en"
            ? this.state.APPLICATION.EN_RANDEVU.API
            : this.state.APPLICATION.SALON_MANAGEMENT.API, //current.api
      app:
        process.env.APP_NAME === "salon"
          ? this.state.APPLICATION.SALON_RANDEVU
          : process.env.APP_NAME === "en"
            ? this.state.APPLICATION.EN_RANDEVU
            : this.state.APPLICATION.SALON_MANAGEMENT, //current.application
      current_flag: this.state.APPLICATION.SALON_RANDEVU.ID
        ? this.state.APPLICATION.SALON_RANDEVU.ID
        : this.state.APPLICATION.EN_RANDEVU.ID, //current.flag
      expected_en_randevu:
        process.env.APP_NAME === "salon"
          ? ["/receipts", "/accounting", "/reports", "/packages", "/products"]
          : [],
      expected_child_en_randevu: ["/risky"],

      t: (key, variables, config) =>
        translate(key, this.state, config, variables), // Multilangual translator function
      state: this.state,
      dispatch: this.dispatch,
      actions: { DateFormatter },
    };

    return (
      <AppContext.Provider value={context}>
        {this.props.children}
      </AppContext.Provider>
    );
  }
}

export default AppContext;
export const AppConsumer = AppContext.Consumer;

const FLAG = {
  SALON_RANDEVU: 1 << 1,
  SALON_MANAGEMENT: 1 << 1,
  EN_RANDEVU: 1 << 2,
};

export const initialState = {
  walletLicensePanelScaffold: true,
  currency: process.env.APP_NAME === "management" ? "$" : null,
  currencyValues: [],

  default_locale: process.env.APP_NAME === "management" ? "en" : "tr",
  locales: { tr, en },

  FLAGS: {
    SALON_RANDEVU: FLAG.SALON_RANDEVU,
    EN_RANDEVU: FLAG.EN_RANDEVU,
    SALON_MANAGEMENT: FLAG.SALON_MANAGEMENT,
  },

  APPLICATION: {
    SALON_RANDEVU: {
      NAME: "Salon Randevu",
      ID: FLAG.SALON_RANDEVU,
      API: process.env.IS_TEST === true
        ? "https://api2.salonrandevu.com/api/v1"
        : "https://api.salonrandevu.com/api/v1",
      WEB: process.env.IS_TEST === true
        ? "https://app2.salonrandevu.com"
        : "https://app.salonrandevu.com",
      EXCEL:
        "https://app.salonrandevu.com/excel/Salonrandevu_MusteriExcel.xlsx",
      PRIVACY: "http://salonrandevu.com/gizlilik-sozlesmesi",
      LOGO: "logo.png",
      INSTAGRAM: "salonrandevu",
      LANGUAGE: false,
      COLORS: {
        //for reference
        PRIMARY: "#3189ec",
        SECONDARY: "rgb(71, 13, 108)",
      },
      SMS_SENDER_NAME: "SalonRANDVU",
    },
    EN_RANDEVU: {
      NAME: "En Randevu",
      ID: FLAG.EN_RANDEVU,
      API: process.env.IS_TEST === true
        ? "https://api2.enrandevu.com/api/v1"
        : "https://api.enrandevu.com/api/v1",
      WEB: process.env.IS_TEST === true
        ? "https://app2.enrandevu.com"
        : "https://app.enrandevu.com",
      EXCEL:
        "https://app.salonrandevu.com/excel/Salonrandevu_MusteriExcel.xlsx",
      PRIVACY: "http://www.enrandevu.com/gizlilik-soz",
      LOGO: "logo_en.png",
      INSTAGRAM: "salonrandevu",
      LANGUAGE: false,
      COLORS: {
        //for reference
        PRIMARY: "#ffa000",
        SECONDARY: "#1e90ff",
      },
      SMS_SENDER_NAME: "Enrandevu",
    },
    SALON_MANAGEMENT: {
      NAME: "Salon Management",
      ID: FLAG.SALON_MANAGEMENT,
      API: process.env.IS_TEST === true
        ? "https://api2.salonmanagementapp.com/api/v1"
        : "https://api.salonmanagementapp.com/api/v1",
      WEB: process.env.IS_TEST === true
        ? "https://app2.salonmanagementapp.com"
        : "https://app.salonmanagementapp.com",
      EXCEL:
        "https://app.salonmanagementapp.com/excel/SalonManagement_CustomerExcel.xlsx",
      PRIVACY: "http://salonrandevu.com/gizlilik-sozlesmesi",
      LOGO: "logo_salonmanagement.png",
      INSTAGRAM: "salonmanagement",
      LANGUAGE: false,
      COLORS: {
        //for reference
        PRIMARY: "#ed1e79",
        SECONDARY: "rgb(71, 13, 108)",
      },
      SMS_SENDER_NAME: "SalonRANDVU",
    },
  },

  is_authenticated: false,
  token_expire_date: null, //Date.now() biçiminde
  token: null,

  user: {
    id: null,
    full_name: null,
    permission: null,
  },

  company_id: null,
  company_shift: {
    start_hour: null,
    end_hour: null,
    slice: null,
  },

  company_services: [],
  company_license: null,

  company_sms_settings: null,
  company_sms_settings_loaded: false,

  company_branches: [],
  company_branch_selected: false,
  company_is_branch: false, // Şube mi
  company_branch_first: {
    id: null, // İşletme ID
    name: null, // İşletme adı
    staffs: [
      {
        id: null,
        full_name: null,
        detail: {
          phone: null,
          mail: null,
        },
      },
    ],
    license: {
      end_date_text: null,
    },
  },

  company_tutorial: null,

  appointment_notification: false,

  sms_credit: null,
  remainingAppointment: null,

  limited_permission: null,

  whatsapp_permission: null,

  online_link: null,

  license_choosen: false,

  dialCode: null,

  balance: [],
  fieldCardTwoTitle: 0,

  api_key: null,

  wp_state: null,

  wp_phone: null,

  checkedWP: true,

  sender: "",

  employeePackages: [],
  is_dealer: null,

  log_type: null,
  log_id: null,

  //currency: null,
  currencyCode: null,
  wp_is_connected: null,

  campaigns: {
    first: {
      url: null,
      timeLeft: null,
      loaded: false
    },
    renewal: {
      url: null,
      timeLeft: null,
      loaded: false
    }
  },
  chatbox_open: false,
};
